import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Clock, AlertTriangle, CheckCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { GameTimeRemaining } from '@/types/gameSystem';

interface EnhancedCountdownTimerProps {
  countdown: GameTimeRemaining;
  variant?: 'compact' | 'detailed';
  className?: string;
}

export const EnhancedCountdownTimer = ({ 
  countdown, 
  variant = 'compact',
  className 
}: EnhancedCountdownTimerProps) => {
  if (countdown.expired) {
    return (
      <Card className={cn("border-destructive bg-destructive/5", className)}>
        <CardContent className="p-4">
          <div className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            <span className="font-semibold">Roster Deadline Expired</span>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            No further roster changes allowed
          </p>
        </CardContent>
      </Card>
    );
  }

  const formatTime = () => {
    if (countdown.days > 0) {
      return `${countdown.days}d ${countdown.hours}h ${countdown.minutes}m`;
    } else if (countdown.hours > 0) {
      return `${countdown.hours}h ${countdown.minutes}m ${countdown.seconds}s`;
    } else {
      return `${countdown.minutes}m ${countdown.seconds}s`;
    }
  };

  const getUrgencyColor = () => {
    if (countdown.total_seconds < 300) return 'destructive'; // Less than 5 minutes
    if (countdown.total_seconds < 1800) return 'secondary'; // Less than 30 minutes
    return 'default';
  };

  if (variant === 'compact') {
    return (
      <Badge variant={getUrgencyColor()} className={cn("flex items-center gap-1", className)}>
        <Clock className="h-3 w-3" />
        {formatTime()} remaining
      </Badge>
    );
  }

  return (
    <Card className={cn("border-primary bg-primary/5", className)}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-primary" />
            <span className="font-semibold">Roster Deadline</span>
          </div>
          <Badge variant={getUrgencyColor()}>
            {formatTime()} remaining
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground mt-2">
          Teams must finalize their rosters before the deadline
        </p>
        
        {countdown.total_seconds < 300 && (
          <div className="mt-3 flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-4 w-4" />
            <span className="text-sm font-medium">Urgent: Less than 5 minutes remaining!</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};